import type { IAttentionStatusApi } from '../api/types'
import type { IAggregatedAttentionStatus, IPartnerAttentionMask } from '../model/types'

import { applyAttentionStatusOffering } from './applyAttentionStatusOffering'
import { decodeAttentionStatusMasks } from './decodeAttentionStatusMasks'
import { isPlanCountedAttentionTitle } from './formatAttentionStatusTitle'

interface IAggregateAttentionStatusesParams {
  /** The catalog every mask is read against. */
  catalog: IAttentionStatusApi[]
  /** The partners in view and their masks — the `Y` of `X of Y`, raised or not. */
  partners: IPartnerAttentionMask[]
  /** Count a `… plans …` title by partner: a distributor plans all its offerings as one. */
  countsPlansByPartner?: boolean
}

/**
 * Rolls a group of partners' masks up into one list: every status at least one partner in view
 * raised, ordered by `sortOrder`, carrying the partners behind it. A status no mask raises is left
 * out.
 *
 * A status is one row across partners, so its wording comes from the catalog, not from a partner.
 * Partners whose mask raises nothing still count towards `scopeSize`: the `Y` is the group the
 * cockpit's filters produced, and shrinking it would make the tile disagree with the partner list
 * beside it.
 *
 * A `… plans …` title counts plans — one per partner and offering mask — so a partner raising it
 * for three offerings is three of them, out of every mask in view — unless `countsPlansByPartner`,
 * where one partner is one plan.
 */
export const aggregateAttentionStatuses = ({
  catalog,
  partners,
  countsPlansByPartner = false,
}: IAggregateAttentionStatusesParams): IAggregatedAttentionStatus[] => {
  const rawTitles = new Map(catalog.map(({ BitValue, Title }) => [BitValue, Title]))
  const byBitValue = new Map<number, IAggregatedAttentionStatus>()
  const planScope = partners.reduce((sum, { masks }) => sum + masks.length, 0)
  const isPlanCounted = (bitValue: number) =>
    !countsPlansByPartner && isPlanCountedAttentionTitle(rawTitles.get(bitValue) ?? '')

  partners.forEach(({ partnerId, masks }) => {
    decodeAttentionStatusMasks({ masks, catalog }).forEach((status) => {
      const aggregated = byBitValue.get(status.bitValue)
      const raisedPlans = Math.max(status.offerings.length, 1)
      const raisedCount = isPlanCounted(status.bitValue) ? raisedPlans : 1

      if (aggregated) {
        aggregated.partnerIds.push(partnerId)
        aggregated.raisedCount += raisedCount

        /* One row across partners, so it is titled for the offering only while every partner
           behind it raised the same one. */
        aggregated.offerings = [...new Set([...aggregated.offerings, ...status.offerings])]
        aggregated.title = applyAttentionStatusOffering(
          rawTitles.get(status.bitValue) ?? status.title,
          aggregated.offerings
        )
        return
      }

      byBitValue.set(status.bitValue, {
        ...status,
        partnerIds: [partnerId],
        scopeSize: partners.length,
        raisedCount,
        raisedScope: isPlanCounted(status.bitValue) ? planScope : partners.length,
      })
    })
  })

  return [...byBitValue.values()].sort((left, right) => left.sortOrder - right.sortOrder)
}
